/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/

import { useSelector } from "react-redux"
import { AppState } from "store/store.type"
import { useMenuV3EditorStatusQuery } from "queries"
import { useEnablePartialMenu } from "hooks/useEnablePartialMenu"

export const useMenuEditorStatus = () => {
  const enablePartialMenu = useEnablePartialMenu()
  const {
    categories,
    isMartDepartmentStructure,
    isFetchingMenu,
    merchantID = "",
    merchantGroupID = "",
    isEditAllowed: reduxIsEditAllowed,
    isCreateItemAllowed: reduxIsCreateItemAllowed,
    isStatusChangeAllowed: reduxIsStatusChangeAllowed,
    menu: {
      isMenuEditorEnabled: reduxIsMenuEditorEnabled,
      isAlcoholSaleAllowed: reduxIsAlcoholSaleAllowed,
      isTobaccoSaleAllowed: reduxIsTobaccoSaleAllowed,
    },
  } = useSelector((state: AppState) => state.menu)

  // Use query data when partial menu is enabled, otherwise use Redux
  const {
    data: editorStatusData,
    isLoading: isEditorStatusLoading,
  } = useMenuV3EditorStatusQuery(
    {
      merchantID,
      merchantGroupID,
    },
    {
      skip: !enablePartialMenu || !merchantID,
    },
  )

  // Get values from query when partial menu enabled, otherwise from Redux
  const isMenuEditorEnabled = enablePartialMenu
    ? !!editorStatusData?.isMenuEditEnabled
    : reduxIsMenuEditorEnabled

  const isAlcoholSaleAllowed = enablePartialMenu
    ? !!editorStatusData?.isAlcoholSaleAllowed
    : reduxIsAlcoholSaleAllowed

  const isTobaccoSaleAllowed = enablePartialMenu
    ? !!editorStatusData?.isTobaccoSaleAllowed
    : reduxIsTobaccoSaleAllowed

  // Compute derived flags (same logic as handleUpdateEditAllowed)
  // When partial menu is enabled, compute from query data
  // When disabled, use Redux values (which are already computed by handleUpdateEditAllowed)
  const hasCategories = isFetchingMenu || categories.length > 0

  const isEditAllowed = enablePartialMenu
    ? isMenuEditorEnabled
    : reduxIsEditAllowed

  const isCreateItemAllowed = enablePartialMenu
    ? isEditAllowed && (isMartDepartmentStructure || hasCategories)
    : reduxIsCreateItemAllowed

  const isStatusChangeAllowed = enablePartialMenu
    ? true
    : reduxIsStatusChangeAllowed

  return {
    isMenuEditorEnabled,
    isEditAllowed,
    isCreateItemAllowed,
    isStatusChangeAllowed,
    isAlcoholSaleAllowed,
    isTobaccoSaleAllowed,
    isEditorStatusLoading,
  }
}
